import { MoreHorizontal, Search, TrendingUp, TrendingDown, Minus } from "lucide-react";

// Consistent palette used across the donut, funnel cards, and table row
// accents so a given stage always reads as the same color everywhere.
export const STAGE_COLORS = ["#38bdf8", "#a78bfa", "#fb923c", "#34d399", "#f472b6", "#facc15", "#60a5fa", "#f87171"];
export const stageColor = (index) => STAGE_COLORS[index % STAGE_COLORS.length];

// Formats a duration in seconds (as returned by the addresses API for
// time-in-stage / time-in-pipeline) into a compact "Xd Yh" / "Xh Ym" / "Xm" string.
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;

  const minutes = Math.floor(s / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
};

export const MoreIcon = () => <MoreHorizontal size={16} className="text-gray-400" />;

// Single source of truth for the stage <-> leads relationship: every stage's
// percentage is "how many of the total leads made it here", not "how many of
// the previous stage made it here". Leads (the first/entry stage) is always
// the 100% baseline that every other number is measured against.
export const pctOfLeads = (value, leadsTotal) => (leadsTotal > 0 ? Math.round(((value || 0) / leadsTotal) * 100) : 0);

export const NoDataFound = ({ label = "No data found" }) => (
  <div className="flex flex-col items-center justify-center h-full gap-2 py-10">
    <div className="w-10 h-10 rounded-full border-2 border-blue-200 flex items-center justify-center">
      <Search size={16} className="text-blue-200" />
    </div>
    <span className="text-gray-400 text-sm font-medium">{label}</span>
  </div>
);

export const Card = ({ className = "", children }) => <div className={`bg-white rounded-xl border border-gray-100 shadow-sm ${className}`}>{children}</div>;

export const CardHeader = ({ title, subtitle, action }) => (
  <div className="flex justify-between items-start mb-2">
    <div className="w-full">
      <div className="text-xs text-gray-500 font-medium text-center">{title}</div>
      {subtitle && <div className="text-[11px] text-gray-400 mt-0.5 text-center">{subtitle}</div>}
    </div>
    {action || (
      <button className="text-gray-400 hover:text-gray-600">
        <MoreIcon />
      </button>
    )}
  </div>
);

// Conversion badge -- a mini progress bar: a grey track the full width of the
// cell, a colored fill sized to `pct` (green/amber/red by the same health
// thresholds as before), and the percentage label centered over the whole
// track -- so the label never shifts even as the fill grows/shrinks.
export const ConversionBadge = ({ pct }) => {
  let fillClasses = "bg-green-400";
  let textClasses = "text-green-900";
  if (pct < 25) {
    fillClasses = "bg-red-400";
    textClasses = "text-red-900";
  } else if (pct < 50) {
    fillClasses = "bg-amber-400";
    textClasses = "text-amber-900";
  }

  const clamped = Math.max(0, Math.min(100, pct));

  return (
    <div className="relative w-full min-w-[56px] h-5 rounded bg-gray-100 overflow-hidden">
      <div className={`absolute inset-y-0 left-0 rounded ${fillClasses} transition-all`} style={{ width: `${clamped}%` }} />
      <span className={`relative z-10 flex items-center justify-center h-full text-[11px] font-semibold ${textClasses}`}>{pct}%</span>
    </div>
  );
};

// Top-of-dashboard summary tiles (total leads, closed, bottleneck stage, etc).
export const KpiCard = ({ label, value, hint, trend }) => {
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const trendClass = trend === "up" ? "text-green-600" : trend === "down" ? "text-red-500" : "text-gray-300";

  return (
    <Card className="p-3.5 flex flex-col justify-between min-w-0">
      <div className="text-[11px] text-gray-500 truncate">{label}</div>
      <div className="flex items-center justify-center space-x-6 mt-1.5">
        <div className="text-2xl font-light text-gray-800 tracking-tight truncate">{value}</div>
        {trend && <TrendIcon size={16} className={`${trendClass} flex-shrink-0 ml-2`} />}
      </div>
      {hint && <div className="text-[11px] text-gray-400 mt-1 truncate">{hint}</div>}
    </Card>
  );
};

export const StageMetricCard = ({ name, total, conversionPct, isFirst, color }) => (
  <Card className="p-3 flex flex-col relative min-w-0">
    <div className="flex items-center gap-1.5 mb-1.5 min-w-0">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      <span className="text-[11px] text-gray-500 truncate" title={name}>
        {name}
      </span>
    </div>
    <div className="text-2xl font-light text-gray-800 tracking-tight">{total}</div>
    <div className="mt-1.5 h-[19px]">{!isFirst && <ConversionBadge pct={conversionPct} />}</div>
  </Card>
);
